import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, ReplaySubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';

import { DigihavenAuthenticationService } from '@digihaven/shared';

import { Logout } from '@actions/authentication.actions';
import { SetTokens } from '@actions/token.actions';
import { UserTokens } from '@common/entities/user-tokens.entity';

import { ApplicationState } from 'state';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
    private userTokens$: Observable<UserTokens>;
    private isRefreshingToken = false;
    private refreshTokenSubject = new ReplaySubject<string | undefined>(1);

    constructor(
        private service: DigihavenAuthenticationService,
        private store: Store<ApplicationState>,
        private router: Router
    ) {
        this.userTokens$ = this.store.select<UserTokens>(state => state.token.userTokens);
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError(error => {
                if (error.status === 401) {
                    return this.handle401(req, next);
                }

                return throwError(error);
            })
        );
    }

    private handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;
            this.refreshTokenSubject.next(undefined);

            return this.refreshTokenAndContinue(req, next);
        } else {
            return this.waitForRefreshAndContinue(req, next);
        }
    }

    private refreshTokenAndContinue(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.userTokens$.pipe(
            take(1),
            switchMap(this.refreshTokenIfPossible.bind(this)),
            catchError(this.handleRefreshTokenError.bind(this)),
            tap(this.handleUserTokens.bind(this)),
            switchMap(() => next.handle(req))
        );
    }

    private refreshTokenIfPossible(userTokens: UserTokens | undefined): Observable<UserTokens> {
        if (!userTokens || !userTokens.refresh_token) {
            return throwError('NO REFRESH TOKEN');
        }

        return this.service.refreshToken(userTokens.refresh_token);
    }

    private waitForRefreshAndContinue(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.refreshTokenSubject.pipe(
            filter(refreshToken => refreshToken !== undefined),
            take(1),
            switchMap(() => next.handle(req))
        );
    }

    private handleUserTokens(userTokens: UserTokens): void {
        this.store.dispatch(new SetTokens(userTokens));
        this.refreshTokenSubject.next(userTokens.refresh_token);
    }

    private handleRefreshTokenError(error: HttpErrorResponse): Observable<never> {
        this.store.dispatch(new Logout());
        this.router.navigate(['/login']);

        return throwError(error);
    }
}
